import { useEffect, useState } from "react";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { getStreetViewStatus, streetViewImageUrl } from "./api";

/**
 * A single Street View reference photo cell (Session 8, board doc §4.8 / §5 "Street View
 * fallback"). Wraps the already-shipped, previously-unwired `providers/streetview.py` scaffold via
 * the new `GET /api/next/streetview/{status,image}` endpoints (`server/amb_next.py`).
 *
 * What this is NOT:
 *  - Not a substitute for a human-uploaded subject photo — this project's own two-tag provenance
 *    rule treats an agent's real photo as the higher-trust source; this cell is reference-only
 *    context, always labeled as such, never presented as if it were the confirmed subject photo.
 *  - Not part of any PDF/DOCX deliverable — `providers/streetview.py`'s `deliverable_license` gate
 *    stays False until counsel confirms Google's Static Street View API terms permit the image
 *    inside a paid customer deliverable rather than on-screen-only display (strict-tier: legality
 *    wins). This component only ever requests the on-screen (`for_deliverable=False`) path — see
 *    `amb_next.streetview_image`'s server-side enforcement of the same rule.
 *  - Not billed/fetched unless the deployment has explicitly turned the provider on
 *    (`VALUBRIEF_STREETVIEW_ENABLED=1` + a configured GCP key) — this component calls `/status`
 *    FIRST and renders nothing (not a broken image, not a spinner that never resolves) when the
 *    provider is off, which is this session's shipped default.
 */
export function StreetViewCell({
  address,
  label,
  heading,
}: {
  address: string;
  label: string;
  /** A section heading to render WITH the photo, when there is one (2026-08-30).
   *
   *  WHY IT LIVES HERE AND NOT IN THE CALLER. `ConfirmGrid` printed "REFERENCE PHOTO (SUBJECT)"
   *  above this component unconditionally, and this component returns `null` whenever the provider
   *  is off — which is the SHIPPED DEFAULT. So on every deployment as configured today, every
   *  subject carried a section header over nothing at all. A heading is a promise that something
   *  follows it; only the component that knows whether anything will can make that promise.
   *
   *  Optional, so the other call sites (one per comparable, inside a table cell) are unchanged. */
  heading?: string;
}) {
  const [status, setStatus] = useState<{ enabled: boolean; attribution: string } | null>(null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (!address.trim()) return; // nothing to look up — skip the status call entirely
    let cancelled = false;
    getStreetViewStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [address]);

  if (!status || !status.enabled || !address.trim()) {
    // Inert (the shipped default), status not yet loaded, or nothing to look up — render nothing
    // rather than a broken image or a placeholder that could be mistaken for real content.
    return null;
  }

  return (
    <>
      {heading && (
        <h3
          style={{
            fontSize: 13,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: colors.textSecondary,
            margin: "0 0 8px",
          }}
        >
          {heading}
        </h3>
      )}
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        gap: 4,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 8,
        background: colors.bgSurface,
        maxWidth: 340,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>{label} — reference photo</span>
      </div>
      {imgFailed ? (
        <p style={{ fontSize: 12, color: colors.textSecondary, margin: 0 }}>No Street View coverage at this address.</p>
      ) : (
        <img
          src={streetViewImageUrl(address)}
          alt={`Street View reference photo near ${address}`}
          onError={() => setImgFailed(true)}
          style={{ width: "100%", borderRadius: 6, display: "block" }}
        />
      )}
      <p style={{ fontSize: 10, color: colors.textSecondary, margin: 0, fontFamily: typeTokens.fontUi }}>
        {status.attribution} — display only, reference context; not the confirmed subject photo, and not part of the
        PDF/DOCX deliverable.
      </p>
    </div>
    </>
  );
}
